'use client'

import Link from 'next/link'
import { Fragment, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { useForm, Controller, useWatch, type Path } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { isAxiosError } from 'axios'

import { cn } from '@/utils/utils'
import { Card, CardContent } from '@/components/ui/card'
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldTitle,
} from '@/components/ui/field'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PhoneInput } from '@/components/ui/phone-input'
import { CepInput } from '@/components/ui/cep-input'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/common/loading-button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useRegisterTenantMutation } from '@/hooks/useAuthQuery'
import { useIbgeCepAutofill } from '@/hooks/useIbgeCepAutofill'
import { formatPhone, stripPhone } from '@/utils/phone'
import { maskCep } from '@/utils/cep'
import { IBGE_STATES, IBGE_MUNICIPALITIES } from '@/data/ibge'
import type {
  RegisterTenantPayload,
  RegisterTenantType,
} from '@/types/RegisterTenantType'
import {
  getRegisterDraft,
  removeRegisterDraft,
  saveRegisterDraft,
  type RegisterDraft,
  type RegisterDraftValues,
} from '@/features/auth/register-draft'
import type { LucideIcon } from 'lucide-react'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Building2Icon,
  CheckIcon,
  HistoryIcon,
  LockIcon,
  MapPinIcon,
  RotateCcwIcon,
  StoreIcon,
  UserRoundIcon,
  UserRoundPlusIcon,
} from 'lucide-react'

const registerSchema = z
  .object({
    type: z.string().min(1, 'Selecione o tipo de cadastro'),
    name: z.string(),
    owner_name: z.string().min(3, 'Informe o nome com pelo menos 3 caracteres'),
    owner_email: z.string().email('E-mail inválido'),
    owner_phone: z.string().min(10, 'Informe um telefone válido'),
    password: z.string().min(8, 'A senha deve ter pelo menos 8 caracteres'),
    password_confirmation: z.string(),
    uf_id: z.string().min(1, 'Selecione a UF'),
    city_id: z.string().min(1, 'Selecione a cidade'),
    address: z.object({
      postal_code: z
        .string()
        .regex(/^\d{8}$/, 'CEP inválido (ex.: 00000-000)'),
      street: z.string().min(1, 'Informe a rua'),
      number: z.string().min(1, 'Informe o número'),
      complement: z.string().optional(),
      neighborhood: z.string().min(1, 'Informe o bairro'),
      city: z.string().min(1, 'Selecione a cidade'),
      state: z.string().min(1, 'Selecione a UF'),
    }),
  })
  .superRefine((data, ctx) => {
    if (data.type === 'cfc' && data.name.trim().length < 3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Informe o nome com pelo menos 3 caracteres',
        path: ['name'],
      })
    }

    if (data.password !== data.password_confirmation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'As senhas não coincidem',
        path: ['password_confirmation'],
      })
    }
  })

type RegisterFormData = z.infer<typeof registerSchema>

const CFC_STEPS = [
  { label: 'Tipo de conta', icon: Building2Icon },
  { label: 'Empresa', icon: StoreIcon },
  { label: 'Responsável', icon: UserRoundIcon },
  { label: 'Endereço', icon: MapPinIcon },
  { label: 'Senha de acesso', icon: LockIcon },
] as const

const AUTONOMOUS_STEPS = [
  { label: 'Tipo de conta', icon: Building2Icon },
  { label: 'Responsável', icon: UserRoundIcon },
  { label: 'Endereço', icon: MapPinIcon },
  { label: 'Senha de acesso', icon: LockIcon },
] as const

const CFC_STEP_FIELDS: Array<Path<RegisterFormData>[]> = [
  ['type'],
  ['name'],
  ['owner_name', 'owner_email', 'owner_phone'],
  [
    'address.postal_code',
    'uf_id',
    'city_id',
    'address.number',
    'address.street',
    'address.neighborhood',
    'address.complement',
  ],
  ['password', 'password_confirmation'],
]

const AUTONOMOUS_STEP_FIELDS: Array<Path<RegisterFormData>[]> = [
  ['type'],
  ['owner_name', 'owner_email', 'owner_phone'],
  [
    'address.postal_code',
    'uf_id',
    'city_id',
    'address.number',
    'address.street',
    'address.neighborhood',
    'address.complement',
  ],
  ['password', 'password_confirmation'],
]

function buildRegisterPayload(data: RegisterFormData): RegisterTenantPayload {
  const ufId = Number(data.uf_id)
  const state = IBGE_STATES.find((s) => s.id === ufId)
  const city = IBGE_MUNICIPALITIES[ufId]?.find((c) => c.id === Number(data.city_id))

  return {
    name: data.type === 'instructor' ? data.owner_name : data.name,
    type: data.type as RegisterTenantType,
    state_id: ufId,
    city_id: Number(data.city_id),
    microregion_id: city?.microregionId ?? 0,
    owner_name: data.owner_name,
    owner_email: data.owner_email,
    owner_phone: data.owner_phone,
    password: data.password,
    password_confirmation: data.password_confirmation,
    contact_type: 'phone',
    contact_value: data.owner_phone,
    contact_label: 'Principal',
    address: {
      postal_code: data.address.postal_code,
      street: data.address.street,
      number: data.address.number,
      complement: data.address.complement || undefined,
      neighborhood: data.address.neighborhood,
      city: data.address.city,
      state: state?.uf ?? data.address.state,
    },
  }
}

function Stepper({
  currentStep,
  onStepClick,
  steps,
}: {
  currentStep: number
  onStepClick?: (step: number) => void
  steps: ReadonlyArray<{ label: string; icon: LucideIcon }>
}) {
  return (
    <TooltipProvider delayDuration={200}>
      <nav aria-label="Progresso do cadastro">
        <ol className="flex items-center gap-1.5">
          {steps.map((step, index) => {
            const isActive = index === currentStep
            const isDone = index < currentStep
            const isClickable = isDone && !!onStepClick
            const Icon = step.icon
            return (
              <Fragment key={step.label}>
                {index > 0 && (
                  <li
                    aria-hidden
                    className={cn(
                      'h-1 flex-1 rounded-full transition-colors',
                      index <= currentStep ? 'bg-primary' : 'bg-border'
                    )}
                  />
                )}
                <li className="flex shrink-0 flex-col items-center gap-1.5">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <button
                          type="button"
                          disabled={!isClickable}
                          onClick={() => onStepClick?.(index)}
                          aria-label={step.label}
                          aria-current={isActive ? 'step' : undefined}
                          className={cn(
                            'rounded-full transition-opacity',
                            isClickable && 'cursor-pointer hover:opacity-80'
                          )}
                        >
                          <span
                            className={cn(
                              'flex size-8 items-center justify-center rounded-full border transition-colors',
                              isActive &&
                                'border-primary bg-primary text-primary-foreground',
                              isDone && 'border-primary bg-primary/10 text-primary',
                              !isActive && !isDone && 'border-border text-muted-foreground'
                            )}
                          >
                            {isDone ? (
                              <CheckIcon className="size-3.5" />
                            ) : (
                              <Icon className="size-3.5" />
                            )}
                          </span>
                        </button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent sideOffset={6}>{step.label}</TooltipContent>
                  </Tooltip>
                </li>
              </Fragment>
            )
          })}
        </ol>
      </nav>
    </TooltipProvider>
  )
}

const EMPTY_DRAFT_VALUES: RegisterDraftValues = {
  type: '',
  name: '',
  owner_name: '',
  owner_email: '',
  owner_phone: '',
  uf_id: '',
  city_id: '',
  address: {
    postal_code: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
  },
}

function toDraftValues(data: RegisterFormData): RegisterDraftValues {
  // Remove as senhas: nunca são salvas no navegador.
  const values: Partial<RegisterFormData> = { ...data }
  delete values.password
  delete values.password_confirmation
  return values as RegisterDraftValues
}

function RegisterDraftPrompt({
  draft,
  onContinue,
  onDiscard,
}: {
  draft: RegisterDraft
  onContinue: () => void
  onDiscard: () => void
}) {
  const { values } = draft
  const isAutonomous = values.type === 'instructor'
  const state = IBGE_STATES.find((s) => s.id === Number(values.uf_id))
  const addressLine = [
    [values.address.street, values.address.number].filter(Boolean).join(', '),
    values.address.complement,
    values.address.neighborhood,
    [values.address.city, state?.uf ?? values.address.state].filter(Boolean).join(' - '),
    maskCep(values.address.postal_code),
  ]
    .filter(Boolean)
    .join(' · ')

  const rows: Array<{ label: string; value?: string }> = [
    {
      label: 'Tipo de conta',
      value: values.type === 'cfc' ? 'CFC' : isAutonomous ? 'Instrutor autônomo' : undefined,
    },
    ...(isAutonomous ? [] : [{ label: 'Nome do CFC', value: values.name }]),
    { label: 'Responsável', value: values.owner_name },
    { label: 'Telefone', value: formatPhone(values.owner_phone) },
    { label: 'E-mail', value: values.owner_email },
    { label: 'Endereço', value: addressLine },
  ]

  const savedAt = new Date(draft.savedAt)
  const savedAtLabel = Number.isNaN(savedAt.getTime())
    ? null
    : savedAt.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })

  return (
    <Card className="overflow-hidden p-0">
      <CardContent className="flex flex-col gap-6 p-6 md:p-8">
        <div className="flex flex-col items-center gap-2 text-center">
          <span className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <HistoryIcon className="size-5" />
          </span>
          <h1 className="text-2xl font-bold">Continuar cadastro?</h1>
          <p className="text-balance text-muted-foreground">
            Encontramos um cadastro iniciado neste navegador
            {savedAtLabel ? ` em ${savedAtLabel}` : ''}. Deseja continuar com
            estes dados?
          </p>
        </div>

        <dl className="divide-y rounded-lg border text-sm">
          {rows.map((row) => (
            <div
              key={row.label}
              className="grid grid-cols-1 gap-1 px-4 py-3 sm:grid-cols-[10rem_1fr] sm:gap-4"
            >
              <dt className="text-muted-foreground">{row.label}</dt>
              <dd className={cn('break-words', !row.value && 'text-muted-foreground')}>
                {row.value || 'Não informado'}
              </dd>
            </div>
          ))}
        </dl>

        <p className="text-center text-xs text-muted-foreground">
          Por segurança, a senha não é salva e precisará ser informada novamente.
        </p>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
          <Button type="button" variant="ghost" onClick={onDiscard}>
            <RotateCcwIcon data-icon="inline-start" /> Começar do zero
          </Button>
          <Button type="button" onClick={onContinue}>
            Continuar cadastro <ArrowRightIcon data-icon="inline-end" />
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

const noopSubscribe = () => () => {}

export function RegisterForm(props: React.ComponentProps<'div'>) {
  // O rascunho fica no localStorage: só é lido após a hidratação no cliente.
  const isClient = useSyncExternalStore(noopSubscribe, () => true, () => false)
  const pendingDraft = useMemo(() => (isClient ? getRegisterDraft() : null), [isClient])
  const [choice, setChoice] = useState<{ values: RegisterDraftValues; step: number } | null>(null)

  if (!isClient) return null

  const initial = choice ?? (pendingDraft ? null : { values: EMPTY_DRAFT_VALUES, step: 0 })

  if (!initial && pendingDraft) {
    return (
      <div className={cn('flex flex-col gap-6', props.className)}>
        <RegisterDraftPrompt
          draft={pendingDraft}
          onContinue={() =>
            setChoice({ values: pendingDraft.values, step: pendingDraft.step })
          }
          onDiscard={() => {
            removeRegisterDraft()
            setChoice({ values: EMPTY_DRAFT_VALUES, step: 0 })
          }}
        />
      </div>
    )
  }

  if (!initial) return null

  return (
    <RegisterFormSteps
      initialValues={initial.values}
      initialStep={initial.step}
      {...props}
    />
  )
}

function RegisterFormSteps({
  className,
  initialValues,
  initialStep,
  ...props
}: React.ComponentProps<'div'> & {
  initialValues: RegisterDraftValues
  initialStep: number
}) {
  const { mutate: registerMutation, isPending, error } = useRegisterTenantMutation()

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      ...EMPTY_DRAFT_VALUES,
      ...initialValues,
      address: { ...EMPTY_DRAFT_VALUES.address, ...initialValues.address },
      password: '',
      password_confirmation: '',
    },
  })

  const accountType = useWatch({ control: form.control, name: 'type' }) as RegisterTenantType | undefined
  const isAutonomous = accountType === 'instructor'
  const steps = isAutonomous ? AUTONOMOUS_STEPS : CFC_STEPS
  const stepFields = isAutonomous ? AUTONOMOUS_STEP_FIELDS : CFC_STEP_FIELDS
  const [step, setStep] = useState(() =>
    Math.min(Math.max(0, initialStep), steps.length - 1)
  )
  const isLastStep = step === steps.length - 1

  // Salva o rascunho a cada alteração (e mudança de passo) para o usuário
  // poder fechar a tela e continuar depois.
  const watchedValues = useWatch({ control: form.control })
  useEffect(() => {
    saveRegisterDraft(toDraftValues(form.getValues()), step)
  }, [form, watchedValues, step])

  // Lógica de endereço (ViaCEP + selects UF/Cidade do IBGE) reutilizada do
  // hook compartilhado — mesma usada na tela de Cadastro da Empresa.
  const { cepLookup, ufId, municipalities, handleUfChange, handleCityChange } =
    useIbgeCepAutofill(form)

  async function handleNext() {
    const valid = await form.trigger(stepFields[step])
    if (valid) {
      setStep((s) => Math.min(steps.length - 1, s + 1))
    }
  }

  function handleBack() {
    setStep((s) => Math.max(0, s - 1))
  }

  function onSubmit(data: RegisterFormData) {
    registerMutation(buildRegisterPayload(data))
  }

  async function handleFormSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (isPending) return
    if (isLastStep) {
      await form.handleSubmit(onSubmit)()
    } else {
      await handleNext()
    }
  }

  const apiErrorMessage = isAxiosError(error)
    ? error.response?.data?.message
    : undefined

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="p-0">
          <form
            className="p-6 md:p-8"
            onSubmit={handleFormSubmit}
          >
            <FieldGroup>
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="text-2xl font-bold">Criar cadastro</h1>
                <p className="text-balance text-muted-foreground">
                  {steps[step].label} — passo {step + 1} de {steps.length}
                </p>
              </div>

              <Stepper currentStep={step} onStepClick={(target) => setStep(target)} steps={steps} />

              <div className="flex w-full min-h-[17rem] flex-col gap-4">
              {step === 0 && (
                <Field>
                  <Controller
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <RadioGroup
                        className="w-full"
                        value={field.value || ''}
                        onValueChange={field.onChange}
                      >
                        <FieldLabel htmlFor="type-cfc">
                          <Field orientation="horizontal">
                            <FieldContent>
                              <FieldTitle>CFC</FieldTitle>
                              <FieldDescription>
                                Autoescola com estrutura completa: alunos,
                                instrutores, agendamentos e contratos.
                              </FieldDescription>
                            </FieldContent>
                            <RadioGroupItem value="cfc" id="type-cfc" />
                          </Field>
                        </FieldLabel>
                        <FieldLabel htmlFor="type-instructor">
                          <Field orientation="horizontal">
                            <FieldContent>
                              <FieldTitle>Instrutor autônomo</FieldTitle>
                              <FieldDescription>
                                Trabalho autônomo: gerencie seus alunos, aulas e
                                disponibilidade.
                              </FieldDescription>
                            </FieldContent>
                            <RadioGroupItem
                              value="instructor"
                              id="type-instructor"
                            />
                          </Field>
                        </FieldLabel>
                      </RadioGroup>
                    )}
                  />
                  <FieldError errors={[form.formState.errors.type]} />
                </Field>
              )}

              {!isAutonomous && step === 1 && (
                <Field>
                  <FieldLabel htmlFor="name">Nome do CFC</FieldLabel>
                  <Input
                    id="name"
                    placeholder="Nome da autoescola / empresa"
                    {...form.register('name')}
                  />
                  <FieldError errors={[form.formState.errors.name]} />
                </Field>
              )}

              {(isAutonomous ? step === 1 : step === 2) && (
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 gap-4 @md/field-group:grid-cols-2">
                    <Field>
                      <FieldLabel htmlFor="owner_name">Responsável</FieldLabel>
                      <Input
                        id="owner_name"
                        placeholder="Nome do responsável"
                        {...form.register('owner_name')}
                      />
                      <FieldError errors={[form.formState.errors.owner_name]} />
                    </Field>

                    <Field>
                      <FieldLabel htmlFor="owner_phone">
                        Telefone do responsável
                      </FieldLabel>
                      <Controller
                        control={form.control}
                        name="owner_phone"
                        render={({ field }) => (
                          <PhoneInput
                            value={field.value}
                            onAccept={(value) => field.onChange(stripPhone(value))}
                            placeholder="(11) 99999-9999"
                          />
                        )}
                      />
                      <FieldError errors={[form.formState.errors.owner_phone]} />
                    </Field>
                  </div>

                  <Field>
                    <FieldLabel htmlFor="owner_email">
                      E-mail do responsável
                    </FieldLabel>
                    <Input
                      id="owner_email"
                      type="email"
                      placeholder="nome@exemplo.com"
                      {...form.register('owner_email')}
                    />
                    <FieldError errors={[form.formState.errors.owner_email]} />
                  </Field>
                </div>
              )}

              {(isAutonomous ? step === 3 : step === 4) && (
                <div className="grid grid-cols-1 gap-4 @md/field-group:grid-cols-2">
                  <Field>
                    <FieldLabel htmlFor="password">Senha</FieldLabel>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Mínimo 8 caracteres"
                      {...form.register('password')}
                    />
                    <FieldError errors={[form.formState.errors.password]} />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="password_confirmation">
                      Confirmar senha
                    </FieldLabel>
                    <Input
                      id="password_confirmation"
                      type="password"
                      placeholder="Repita a senha"
                      {...form.register('password_confirmation')}
                    />
                    <FieldError
                      errors={[form.formState.errors.password_confirmation]}
                    />
                  </Field>
                </div>
              )}

              {(isAutonomous ? step === 2 : step === 3) && (
                <div className="grid grid-cols-1 gap-4 @md/field-group:grid-cols-2">
                  <Field>
                    <FieldLabel htmlFor="postal_code">CEP</FieldLabel>
                    <Controller
                      control={form.control}
                      name="address.postal_code"
                      render={({ field }) => (
                        <CepInput
                          id="postal_code"
                          value={field.value}
                          isLoading={cepLookup.isLoading}
                          onSearch={cepLookup.search}
                          onAccept={(value) => field.onChange(value)}
                        />
                      )}
                    />
                    <FieldError
                      errors={[form.formState.errors.address?.postal_code]}
                    />
                  </Field>

                  <Field>
                    <FieldLabel>UF</FieldLabel>
                    <Controller
                      control={form.control}
                      name="uf_id"
                      render={({ field }) => (
                        <Select
                          key={String(field.value ?? '')}
                          onValueChange={handleUfChange}
                          value={field.value || undefined}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Selecione a UF" />
                          </SelectTrigger>
                          <SelectContent>
                            {IBGE_STATES.map((state) => (
                              <SelectItem
                                key={state.id}
                                value={String(state.id)}
                              >
                                {state.uf} - {state.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    <FieldError errors={[form.formState.errors.uf_id]} />
                  </Field>

                  <Field>
                    <FieldLabel>Cidade</FieldLabel>
                    <Controller
                      control={form.control}
                      name="city_id"
                      render={({ field }) => (
                        <Select
                          key={String(field.value ?? '')}
                          onValueChange={handleCityChange}
                          value={field.value || undefined}
                          disabled={!ufId}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Selecione a cidade" />
                          </SelectTrigger>
                          <SelectContent>
                            {municipalities.map((city) => (
                              <SelectItem
                                key={city.id}
                                value={String(city.id)}
                              >
                                {city.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    <FieldError errors={[form.formState.errors.city_id]} />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="number">Número</FieldLabel>
                    <Input
                      id="number"
                      placeholder="Número"
                      {...form.register('address.number')}
                    />
                    <FieldError
                      errors={[form.formState.errors.address?.number]}
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="street">Rua</FieldLabel>
                    <Input
                      id="street"
                      placeholder="Rua / Avenida"
                      {...form.register('address.street')}
                    />
                    <FieldError
                      errors={[form.formState.errors.address?.street]}
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="neighborhood">Bairro</FieldLabel>
                    <Input
                      id="neighborhood"
                      placeholder="Bairro"
                      {...form.register('address.neighborhood')}
                    />
                    <FieldError
                      errors={[form.formState.errors.address?.neighborhood]}
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="complement">
                      Complemento (opcional)
                    </FieldLabel>
                    <Input
                      id="complement"
                      placeholder="Complemento"
                      {...form.register('address.complement')}
                    />
                    <FieldError
                      errors={[form.formState.errors.address?.complement]}
                    />
                  </Field>
                </div>
              )}
              </div>

              <input type="hidden" {...form.register('address.city')} />
              <input type="hidden" {...form.register('address.state')} />

              {apiErrorMessage && (
                <span className="text-sm text-destructive">
                  {apiErrorMessage}
                </span>
              )}

              <div className="flex items-center justify-between gap-3">
                {step > 0 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleBack}
                    disabled={isPending}
                  >
                    <ArrowLeftIcon data-icon="inline-start" /> Voltar
                  </Button>
                ) : (
                  <span />
                )}

                {isLastStep ? (
                  <LoadingButton type="submit" loading={isPending}>
                    <UserRoundPlusIcon data-icon="inline-start" /> Criar conta
                  </LoadingButton>
                ) : (
                  <Button type="submit" disabled={isPending}>
                    Continuar <ArrowRightIcon data-icon="inline-end" />
                  </Button>
                )}
              </div>

              <FieldDescription className="text-center">
                Já tem uma conta? <Link href="/login">Entrar</Link>
              </FieldDescription>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}